import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { X, Eye, Volume2, VolumeX } from "lucide-react";
import axios from "axios";
import Starfield from "@/components/Starfield";
import SeekerForm from "@/components/SeekerForm";
import MemberLogin from "@/components/MemberLogin";
import { PROJECTS, ACCENTS } from "@/data/projects";
import { soundscapeOn, soundscapeOff, chime, candleFlicker } from "@/components/Soundscape";

// constellation positions — records flank the long hallway; mobile stacks them
const STAR_POS_DESKTOP = {
  films: { x: 16, y: 38 },
  books: { x: 84, y: 38 },
  "tv-series": { x: 50, y: 66 },
};
const STAR_POS_MOBILE = {
  films: { x: 50, y: 40 },
  "tv-series": { x: 50, y: 59 },
  books: { x: 50, y: 78 },
};

const DUST = [
  { x: 8, y: 28, s: 1.6, link: 0 },
  { x: 28, y: 24, s: 1.3, link: 0 },
  { x: 10, y: 70, s: 1.5, link: 0 },
  { x: 34, y: 88, s: 1.2, link: 1 },
  { x: 66, y: 88, s: 1.5, link: 1 },
  { x: 40, y: 70, s: 1.1, link: 1 },
  { x: 72, y: 24, s: 1.3, link: 2 },
  { x: 92, y: 64, s: 1.7, link: 2 },
  { x: 94, y: 20, s: 1.1, link: null },
  { x: 6, y: 88, s: 1.2, link: null },
  { x: 50, y: 14, s: 1.0, link: null },
  { x: 78, y: 90, s: 1.3, link: null },
  { x: 16, y: 12, s: 1.0, link: 0 },
  { x: 24, y: 46, s: 0.9, link: 0 },
  { x: 62, y: 8, s: 1.1, link: 1 },
  { x: 90, y: 40, s: 1.0, link: 1 },
  { x: 40, y: 58, s: 0.9, link: 2 },
  { x: 60, y: 84, s: 1.1, link: 2 },
  { x: 96, y: 86, s: 0.8, link: null },
  { x: 3, y: 60, s: 0.9, link: null },
  { x: 45, y: 6, s: 0.8, link: null },
  { x: 55, y: 94, s: 1.0, link: null },
];

/* faint celestial figures etched into the sky, slowly breathing */
const PATTERNS = [
  { pts: [[8, 12], [14, 6], [21, 11], [14, 16]], edges: [[0, 1], [1, 2], [2, 3], [3, 0], [0, 2]], dur: 9 },
  { pts: [[66, 9], [72, 4], [79, 8], [72, 13]], edges: [[0, 1], [1, 2], [2, 3], [3, 0]], dur: 11 },
  { pts: [[68, 82], [75, 76], [82, 81], [89, 75], [95, 80]], edges: [[0, 1], [1, 2], [2, 3], [3, 4]], dur: 13 },
  { pts: [[5, 44], [11, 37], [17, 42]], edges: [[0, 1], [1, 2]], dur: 10 },
];

const CelestialPatterns = () => (
  <svg
    data-testid="celestial-patterns"
    className="pointer-events-none absolute inset-0 z-[5] h-full w-full"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    {PATTERNS.map((p, pi) => (
      <motion.g
        key={pi}
        animate={{ opacity: [0.2, 0.85, 0.2] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: pi * 1.7 }}
      >
        {p.edges.map(([a, b], ei) => (
          <line
            key={ei}
            x1={p.pts[a][0]} y1={p.pts[a][1]}
            x2={p.pts[b][0]} y2={p.pts[b][1]}
            stroke="#e8c95a"
            strokeOpacity="0.16"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {p.pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="0.22" fill="#f0d98c" fillOpacity="0.8" />
        ))}
      </motion.g>
    ))}
  </svg>
);

const useIsMobile = () => {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = () => setM(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
};

const RecordStar = ({ project, index, hovered, setHovered, pos, onEnter }) => {
  const active = hovered === index;
  const accent = ACCENTS[project.slug];

  return (
    <div
      className="absolute z-20"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6 + index * 0.45, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <Link
          to={`/projects/${project.slug}`}
          data-testid={`record-star-${project.slug}`}
          onClick={(e) => onEnter(e, project, index)}
          onMouseEnter={() => { setHovered(index); chime(index); }}
          onMouseLeave={() => setHovered(null)}
          className="group flex flex-col items-center"
          aria-label={`Enter ${project.title}`}
        >
          <div className="relative flex h-20 w-20 items-center justify-center md:h-24 md:w-24">
            {/* halo in the record's own light */}
            <span
              className={`absolute inset-0 rounded-full transition-opacity duration-700 ${active ? "opacity-80" : "opacity-30"}`}
              style={{ background: `radial-gradient(circle, ${accent.soft} 0%, ${accent.halo} 40%, transparent 68%)` }}
            />
            {/* portal ring — opens when touched */}
            <motion.span
              className="absolute inset-1 rounded-full border"
              style={{ borderColor: accent.main }}
              animate={active ? { scale: [0.85, 2.1], opacity: [0.75, 0] } : { scale: 0.85, opacity: 0 }}
              transition={active ? { duration: 1.4, repeat: Infinity, ease: "easeOut" } : { duration: 0.3 }}
            />
            {/* core star */}
            <motion.span
              animate={{ scale: active ? 1.6 : [1, 1.25, 1] }}
              transition={active ? { duration: 0.5 } : { duration: 3 + index, repeat: Infinity, ease: "easeInOut" }}
              className="relative block h-1.5 w-1.5 rounded-full bg-[#fff7d6] md:h-2 md:w-2"
              style={{ boxShadow: `0 0 9px 3px ${accent.soft}, 0 0 24px 8px ${accent.halo}` }}
            />
            {/* cross flares */}
            <motion.span
              animate={{ opacity: active ? 0.9 : [0.25, 0.55, 0.25] }}
              transition={active ? { duration: 0.4 } : { duration: 4 + index * 0.7, repeat: Infinity, ease: "easeInOut" }}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <span className="absolute h-px w-9 md:w-11" style={{ background: `linear-gradient(90deg, transparent, ${accent.main}, transparent)` }} />
              <span className="absolute h-9 w-px md:h-11" style={{ background: `linear-gradient(180deg, transparent, ${accent.main}, transparent)` }} />
              <span className="absolute h-px w-4 rotate-45" style={{ background: "linear-gradient(90deg, transparent, rgba(240,215,138,0.4), transparent)" }} />
              <span className="absolute h-4 w-px rotate-45" style={{ background: "linear-gradient(180deg, transparent, rgba(240,215,138,0.4), transparent)" }} />
            </motion.span>
          </div>

          <span
            className="font-serif-engraved gold-engraved-deep mt-3 whitespace-nowrap text-sm font-semibold uppercase tracking-[0.3em] transition-all duration-500 sm:text-base md:text-lg md:tracking-[0.34em]"
            style={{ transform: active ? "scale(1.12)" : "scale(1)" }}
          >
            {project.title}
          </span>
        </Link>
      </motion.div>
    </div>
  );
};

const ConstellationLines = ({ hovered, order, dust, accents }) => (
  <svg
    data-testid="constellation-lines"
    className="pointer-events-none absolute inset-0 z-10 h-full w-full"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    {dust.filter((d) => d.link !== null).map((d, i) => {
      const r = order[d.link];
      return (
        <line
          key={`dust-${i}`}
          x1={d.x} y1={d.y} x2={r.x} y2={r.y}
          stroke={accents[d.link].main}
          strokeOpacity={hovered === d.link ? 0.38 : 0.1}
          strokeWidth="0.6"
          vectorEffect="non-scaling-stroke"
          strokeDasharray="2 4"
          style={{ transition: "stroke-opacity 0.6s" }}
        />
      );
    })}
    {order.slice(0, -1).map((a, i) => {
      const b = order[i + 1];
      const lit = hovered === i || hovered === i + 1;
      return (
        <motion.line
          key={`path-${i}`}
          x1={a.x} y1={a.y} x2={b.x} y2={b.y}
          stroke={lit ? accents[i].main : "#c9a227"}
          strokeOpacity={lit ? 0.8 : 0.28}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ delay: 2.2 + i * 0.5, duration: 1.6, ease: "easeInOut" }}
          style={{ transition: "stroke-opacity 0.5s, stroke 0.5s" }}
        />
      );
    })}
    {dust.map((d, i) => (
      <motion.path
        key={`dot-${i}`}
        d={`M ${d.x} ${d.y - d.s * 0.55} L ${d.x + d.s * 0.16} ${d.y - d.s * 0.16} L ${d.x + d.s * 0.55} ${d.y} L ${d.x + d.s * 0.16} ${d.y + d.s * 0.16} L ${d.x} ${d.y + d.s * 0.55} L ${d.x - d.s * 0.16} ${d.y + d.s * 0.16} L ${d.x - d.s * 0.55} ${d.y} L ${d.x - d.s * 0.16} ${d.y - d.s * 0.16} Z`}
        fill={i % 3 === 0 ? "#e8c95a" : "#fdfbf7"}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0.12, 0.6, 0.12] }}
        transition={{ duration: 3 + (i % 4), repeat: Infinity, delay: i * 0.4 }}
      />
    ))}
  </svg>
);

/* every seeker who joins becomes a tiny star in the sky */
const SeekerStars = ({ count }) => {
  const n = Math.min(count, 60);
  const stars = [];
  for (let i = 0; i < n; i++) {
    stars.push({ x: 4 + ((i * 61.8) % 92), y: 8 + ((i * 37.71) % 78), i });
  }
  if (!n) return null;
  return (
    <svg
      data-testid="seeker-stars"
      className="pointer-events-none absolute inset-0 z-[15] h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {stars.map(({ x, y, i }) => (
        <motion.path
          key={i}
          d={`M ${x} ${y - 0.18} L ${x + 0.05} ${y - 0.05} L ${x + 0.18} ${y} L ${x + 0.05} ${y + 0.05} L ${x} ${y + 0.18} L ${x - 0.05} ${y + 0.05} L ${x - 0.18} ${y} L ${x - 0.05} ${y - 0.05} Z`}
          fill="#e8c95a"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.2, 0.7, 0.2] }}
          transition={{ duration: 2.6 + (i % 3), repeat: Infinity, delay: i * 0.17 }}
        />
      ))}
    </svg>
  );
};

const ShootingStar = () => {
  const [shot, setShot] = useState(null);

  useEffect(() => {
    let t1, t2;
    const schedule = (delay) => {
      t1 = setTimeout(() => {
        setShot({ id: Date.now(), top: 6 + Math.random() * 30, left: 35 + Math.random() * 45 });
        t2 = setTimeout(() => setShot(null), 1600);
        schedule(24000 + Math.random() * 30000);
      }, delay);
    };
    schedule(9000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <AnimatePresence>
      {shot && (
        <motion.div
          key={shot.id}
          className="pointer-events-none absolute z-10"
          style={{ top: `${shot.top}%`, left: `${shot.left}%` }}
          initial={{ x: 0, y: 0, opacity: 0 }}
          animate={{ x: "-38vw", y: "16vh", opacity: [0, 1, 1, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut", times: [0, 0.15, 0.7, 1] }}
          aria-hidden="true"
        >
          <div
            className="h-[2px] w-36 rounded-full"
            style={{
              background: "linear-gradient(90deg, #fff7d6 0%, rgba(240,215,138,0.7) 30%, transparent 100%)",
              transform: "rotate(160deg)",
              transformOrigin: "right center",
              boxShadow: "0 0 12px 2px rgba(255,240,190,0.5)",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const SeekerPortal = ({ open, onClose, onJoined }) => (
  <AnimatePresence>
    {open && (
      <div className="fixed inset-0 z-[80] flex items-center justify-center px-5" data-testid="seeker-portal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/85 backdrop-blur-md"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.93, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 12 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-lg border border-[#c9a227]/30 bg-[#0a0f0a] px-6 py-12 text-center shadow-[0_0_80px_rgba(201,162,39,0.12)] md:px-12"
        >
          <button
            data-testid="seeker-portal-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 text-[#fdfbf7]/40 transition-colors duration-300 hover:text-[#e8c95a]"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#c9a227]">The Inner Circle</p>
          <h2 className="font-serif-engraved mt-3 text-3xl font-light uppercase tracking-wide sm:text-4xl">
            Become a <span className="gold-shimmer">Seeker</span>
          </h2>
          <p className="mx-auto mt-5 max-w-sm text-sm font-light italic leading-relaxed text-[#fdfbf7]/60">
            “A private society for those who were never meant to fit into the ordinary world.”
          </p>
          <div className="mt-8">
            <SeekerForm onJoined={onJoined} />
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

const Home = () => {
  const [hovered, setHovered] = useState(null);
  const [sound, setSound] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [portal, setPortal] = useState(null);
  const [seekers, setSeekers] = useState(0);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // the whole universe breathes with the cursor — or tilts with the phone
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const worldX = useTransform(sx, (v) => v * -26);
  const worldY = useTransform(sy, (v) => v * -16);
  const mistX = useTransform(sx, (v) => v * -12);
  const starX = useTransform(sx, (v) => v * 16);
  const starY = useTransform(sy, (v) => v * 10);

  const starPos = isMobile ? STAR_POS_MOBILE : STAR_POS_DESKTOP;
  const order = PROJECTS.map((p) => starPos[p.slug]);
  const accents = PROJECTS.map((p) => ACCENTS[p.slug]);
  const dust = isMobile ? DUST.slice(0, 5) : DUST;

  const fetchSeekers = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seekers/count`);
      setSeekers(r.data.seekers);
    } catch {}
  };
  useEffect(() => { fetchSeekers(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.gamma == null || e.beta == null) return;
      mx.set(Math.max(-1, Math.min(1, e.gamma / 28)));
      my.set(Math.max(-1, Math.min(1, (e.beta - 45) / 28)));
    };
    const enable = async () => {
      try {
        if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
          const res = await DeviceOrientationEvent.requestPermission();
          if (res !== "granted") return;
        }
        window.addEventListener("deviceorientation", handler);
      } catch {}
    };
    const onFirstTouch = () => {
      enable();
      window.removeEventListener("touchstart", onFirstTouch);
    };
    window.addEventListener("touchstart", onFirstTouch);
    enable();
    return () => {
      window.removeEventListener("deviceorientation", handler);
      window.removeEventListener("touchstart", onFirstTouch);
    };
  }, [mx, my]);

  const toggleSound = () => {
    if (sound) { soundscapeOff(); setSound(false); }
    else { soundscapeOn(); setSound(true); }
  };

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 2);
    my.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  const enterProject = (e, project, index) => {
    e.preventDefault();
    if (portal) return;
    candleFlicker();
    const rect = e.currentTarget.getBoundingClientRect();
    setPortal({
      cx: rect.left + rect.width / 2,
      cy: rect.top + rect.height / 2,
      accent: ACCENTS[project.slug],
    });
    setTimeout(() => navigate(`/projects/${project.slug}`), 1050);
  };

  return (
    <main
      data-testid="landing-page"
      onMouseMove={onMouseMove}
      className="glow-realm relative overflow-hidden bg-[#060b07] md:h-screen"
    >
      {/* the world — image and its shadows are one body, moving together */}
      <motion.div className="absolute -inset-10" style={{ x: worldX, y: worldY }}>
        <div className="animate-kenburns absolute inset-0">
          <picture className="block h-full w-full">
            <source media="(max-width: 767px)" srcSet="/assets/universe_library_mobile.webp" />
            <img
              src="/assets/universe_library_desktop.webp"
              alt=""
              onError={(e) => { e.currentTarget.src = "/assets/world_isis_temple.webp"; }}
              className="h-full w-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-black/25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060b07]/70 via-[#060b07]/10 to-[#060b07]/80" />
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse 70% 55% at 50% 45%, transparent 30%, rgba(6,11,7,0.7) 100%)" }}
          />
        </div>
      </motion.div>

      {/* breathing golden mist */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        style={{ x: mistX }}
        animate={{ opacity: [0.25, 0.55, 0.25] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 45% 35% at 28% 62%, rgba(201,162,39,0.1), transparent 70%), radial-gradient(ellipse 40% 30% at 74% 28%, rgba(80,140,90,0.1), transparent 70%)",
          }}
        />
      </motion.div>

      <Starfield density={80} />

      {/* constellation — foreground, sails with the cursor and slowly wheels overhead (desktop) */}
      <motion.div className="absolute inset-0 hidden md:block" style={{ x: starX, y: starY }}>
        <CelestialPatterns />
        <SeekerStars count={seekers} />
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: [-0.8, 0.8] }}
          transition={{ duration: 80, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          style={{ transformOrigin: "50% 45%" }}
        >
          <ConstellationLines hovered={hovered} order={order} dust={dust} accents={accents} />
          {PROJECTS.map((p, i) => (
            <RecordStar
              key={p.slug}
              project={p}
              index={i}
              hovered={hovered}
              setHovered={setHovered}
              pos={starPos[p.slug]}
              onEnter={enterProject}
            />
          ))}
        </motion.div>
      </motion.div>
      <ShootingStar />

      {/* the mobile hall — an app-like stroll through the universe */}
      <section data-testid="mobile-app" className="relative z-10 px-4 pb-32 pt-8 md:hidden">
        <div className="flex flex-col items-center pt-4">
          <motion.img
            src="/assets/logo_transparent.webp"
            alt="Celestial Studios"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="h-24 w-auto drop-shadow-[0_0_30px_rgba(232,201,90,0.45)]"
          />
        </div>

        <motion.div
          data-testid="mobile-hero-card"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-7 h-[44vh] overflow-hidden rounded-[2rem] border border-[#c9a227]/20"
        >
          <img src="/assets/universe_library_mobile.webp" alt="" className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060b07]/30 via-transparent to-[#060b07]/95" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-left">
            <p className="font-serif-engraved gold-engraved text-lg font-semibold uppercase leading-snug tracking-[0.14em]">
              Production House of<br />Film, TV &amp; Literature
            </p>
            <p className="mt-2 text-[9px] uppercase tracking-[0.3em] text-[#fdfbf7]/50">
              {seekers} seekers among the stars
            </p>
          </div>
        </motion.div>

        <div className="mt-9 flex items-center gap-3 px-1">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#c9a227]/40" />
          <p className="text-[10px] uppercase tracking-[0.45em] text-[#c9a227]/80">Our Stories</p>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#c9a227]/40" />
        </div>

        <div className="mt-5 flex flex-col gap-4">
          {PROJECTS.map((p, i) => (
            <motion.button
              key={p.slug}
              data-testid={`mobile-record-${p.slug}`}
              onClick={() => { candleFlicker(); navigate(`/projects/${p.slug}`); }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="group relative h-52 overflow-hidden rounded-[1.75rem] border border-[#c9a227]/15 text-left"
            >
              <img
                src={p.keyArt}
                alt=""
                className="h-full w-full object-cover transition-transform duration-700 group-active:scale-105"
                style={{ objectPosition: p.artPosMobile || p.artPos || "50% 50%" }}
              />
              {p.dim && <div className="absolute inset-0 bg-black/55" />}
              <div className="absolute inset-0 bg-gradient-to-b from-[#060b07]/25 via-transparent to-[#060b07]/95" />
              <span className="absolute right-4 top-4 rounded-full border border-[#c9a227]/35 bg-black/55 px-3 py-1 text-[8px] uppercase tracking-[0.25em] text-[#e8c95a]/90 backdrop-blur-sm">
                {p.medium}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="font-serif-engraved gold-engraved mt-1 text-2xl font-semibold uppercase tracking-[0.08em]">{p.title}</p>
              </div>
            </motion.button>
          ))}
        </div>

        <motion.button
          data-testid="mobile-seeker-card"
          onClick={() => setPortalOpen(true)}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 flex w-full items-center gap-4 rounded-[1.75rem] border border-[#c9a227]/25 bg-black/50 p-5 text-left backdrop-blur-sm"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#c9a227]/40 bg-[#c9a227]/10">
            <Eye size={20} strokeWidth={1.3} className="text-[#e8c95a]" />
          </span>
          <span>
            <span className="font-serif-engraved block text-base text-[#fdfbf7]/90">Become a Seeker</span>
            <span className="mt-0.5 block text-[9px] uppercase tracking-[0.3em] text-[#c9a227]/60">Take the oath · enter the Society</span>
          </span>
        </motion.button>
      </section>

      {/* logo */}
      <motion.div
        initial={{ opacity: 0, y: -18, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        className="pointer-events-none absolute inset-x-0 top-5 z-30 hidden flex-col items-center md:flex"
      >
        <motion.img
          src="/assets/logo_transparent.webp"
          alt="Celestial Studios"
          data-testid="landing-logo"
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="h-28 w-auto drop-shadow-[0_0_40px_rgba(232,201,90,0.5)] md:h-36"
        />
      </motion.div>

      {/* the house line — cream, bold, dead centre on web */}
      <motion.div className="pointer-events-none absolute inset-x-0 top-[44%] z-20 hidden -translate-y-1/2 justify-center px-6 md:flex">
        <div className="relative">
          <div
            className="absolute -inset-14"
            style={{ background: "radial-gradient(ellipse 66% 70% at 50% 50%, rgba(3,6,4,0.92), transparent 74%)", filter: "blur(12px)" }}
            aria-hidden="true"
          />
          <motion.h2
            data-testid="landing-center-title"
            initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ delay: 2, duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-engraved gold-engraved relative inline-block px-12 py-5 text-center text-xl font-semibold uppercase leading-snug tracking-[0.28em] md:text-2xl md:tracking-[0.38em] lg:text-3xl"
          >
            <span className="block">Production House of</span>
            <span className="mt-2 block">Film, TV &amp; Literature</span>
          </motion.h2>
        </div>
      </motion.div>

      {/* sound toggle */}
      <motion.button
        data-testid="sound-toggle-button"
        onClick={toggleSound}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        aria-label={sound ? "Silence the cosmos" : "Hear the cosmos"}
        className={`fixed bottom-7 left-7 z-40 flex h-12 w-12 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-500 ${
          sound
            ? "border-[#c9a227]/70 bg-[#c9a227]/15 text-[#e8c95a] shadow-[0_0_30px_rgba(201,162,39,0.35)]"
            : "border-[#c9a227]/35 bg-black/50 text-[#c9a227]/80 hover:border-[#c9a227]/70 hover:text-[#e8c95a]"
        }`}
      >
        {sound ? <Volume2 size={19} strokeWidth={1.3} /> : <VolumeX size={19} strokeWidth={1.3} />}
      </motion.button>

      {/* members' key */}
      <MemberLogin />

      {/* seeker eye */}
      <motion.button
        data-testid="seeker-eye-trigger"
        onClick={() => setPortalOpen(true)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        aria-label="Become a Seeker"
        className="group fixed bottom-7 right-7 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-[#c9a227]/35 bg-black/50 text-[#c9a227]/80 backdrop-blur-md transition-all duration-500 hover:border-[#c9a227]/70 hover:text-[#e8c95a] hover:shadow-[0_0_30px_rgba(201,162,39,0.35)]"
      >
        <Eye size={19} strokeWidth={1.3} className="transition-transform duration-500 group-hover:scale-110" />
      </motion.button>

      <SeekerPortal open={portalOpen} onClose={() => setPortalOpen(false)} onJoined={fetchSeekers} />

      {/* portal transition — the star swallows the sky in its own colour */}
      <AnimatePresence>
        {portal && (
          <motion.div
            data-testid="portal-transition"
            className="fixed z-[90] rounded-full"
            style={{
              left: portal.cx,
              top: portal.cy,
              x: "-50%",
              y: "-50%",
              background: `radial-gradient(circle, #fff7d6 0%, ${portal.accent.main} 18%, ${portal.accent.halo} 38%, #101a10 62%, #060b07 85%)`,
            }}
            initial={{ width: 0, height: 0, opacity: 1 }}
            animate={{ width: "320vmax", height: "320vmax" }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.05, ease: [0.7, 0, 0.3, 1] }}
          />
        )}
      </AnimatePresence>
    </main>
  );
};

export default Home;
